<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="questionForm" @submit="editQuestion" :validation-schema="questionSchema">
        <div class="row">
          <!-- Contenu -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Contenu <span class="text-danger">*</span>
              </label>
              <Field
                name="contenu"
                type="text"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le contenu de la question"
              />
              <ErrorMessage name="contenu" class="text-danger" />
            </div>
          </div>

          <!-- Type -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type <span class="text-danger">*</span>
              </label>
              <Field name="type" v-slot="{ field }">
                <Multiselect
                  :options="typeOptions"
                  :searchable="true"
                  placeholder="Sélectionner le type"
                  v-model="field.value"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="type" class="text-danger" />
            </div>
          </div>

          <!-- Boutons -->
          <div class="col-md-12 mt-20">
            <div class="d-flex align-items-center gap-3">
              <button
                type="submit"
                class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 bg-success"
              >
                Modifier la question
              </button>
              <router-link
                to="/liste-questions"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'EditQuestion',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const questionForm = ref<InstanceType<typeof Form> | null>(null);
    const id = route.params.id as string;

    // Schéma de validation (sans sondage)
    const questionSchema = Yup.object().shape({
      contenu: Yup.string().required('Le contenu est obligatoire'),
      type: Yup.string().required('Le type est obligatoire'),
    });

    // Options du type de question
    const typeOptions = [
      { value: 'choix_multiple', label: 'Choix multiple' },
      { value: 'texte_libre', label: 'Texte libre' },
      { value: 'oui_non', label: 'Oui/Non' },
    ];

    // Chargement des données actuelles de la question
    const loadQuestion = async () => {
      try {
        const { data } = await ApiService.get(`/questions/${id}`);
        const question = data.data;

        questionForm.value?.setValues({
          contenu: question.contenu,
          type: question.type,
        });
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement de la question');
      }
    };

    // Soumission du formulaire
    const editQuestion = async (values: any) => {
      try {
        const { data } = await ApiService.put(`/questions/${id}`, values);

        if (data.code === 200) {
          Swal.fire({
            text: data.message || 'Question modifiée avec succès',
            icon: 'success',
            confirmButtonText: 'OK',
            heightAuto: false,
            customClass: { confirmButton: 'btn btn-primary fw-semibold' },
          });
          router.push({ name: 'ListeQuestionPage' });
        }
      } catch (err: any) {
        Swal.fire({
          text: err.response?.data?.message || 'Erreur lors de la modification',
          icon: 'error',
          confirmButtonText: 'Réessayer',
          heightAuto: false,
          customClass: { confirmButton: 'btn btn-danger fw-semibold' },
        });
      }
    };

    onMounted(() => {
      loadQuestion();
    });

    return {
      questionSchema,
      questionForm,
      typeOptions,
      editQuestion,
    };
  },
});
</script>

<style src="@vueform/multiselect/themes/default.css"></style>